import { useState, type ReactNode } from 'react';
import type { ChatLabels, FeedbackVote, Message } from '../types';
import { cx, formatTime } from '../utils';
import { AttachmentChip } from './AttachmentChip';
import { FeedbackButtons } from './FeedbackButtons';
import { Markdown } from './Markdown';
import { AlertIcon, CheckIcon, CopyIcon } from './icons';

interface MessageBubbleProps {
  message: Message;
  labels: ChatLabels;
  markdown: boolean;
  avatar?: ReactNode;
  renderMessage?: (message: Message) => ReactNode;
  onVote: (vote: FeedbackVote, comment?: string) => void;
}

export function MessageBubble({
  message,
  labels,
  markdown,
  avatar,
  renderMessage,
  onVote,
}: MessageBubbleProps) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';
  const isError = message.status === 'error';
  const isStreaming = message.status === 'streaming' || message.status === 'pending';
  const hasContent = message.content.trim() !== '';

  const copy = () => {
    void navigator.clipboard?.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  // `renderMessage` may decline a message by returning null/undefined, in which
  // case the default rendering applies. That makes it usable for partial
  // overrides — "custom bubble for pending assistant messages, standard markdown
  // for everything else" — instead of forcing a consumer that wants to change one
  // case to reimplement the error and markdown branches, and with them the
  // DOMPurify sanitisation that makes third-party content safe to render.
  const custom = renderMessage ? renderMessage(message) : null;
  const hasCustom = custom !== null && custom !== undefined && custom !== false;

  const body = hasCustom ? (
    custom
  ) : isError ? (
    <span className="cbw-bubble__error">
      <AlertIcon width={15} height={15} />
      {message.error || labels.errorGeneric}
    </span>
  ) : markdown && !isUser ? (
    <Markdown content={message.content} copyLabel={labels.copy} copiedLabel={labels.copied} />
  ) : (
    <span className="cbw-bubble__text">{message.content}</span>
  );

  return (
    <div className={cx('cbw-msg', isUser ? 'cbw-msg--user' : 'cbw-msg--bot')}>
      {!isUser && <div className="cbw-msg__avatar">{avatar}</div>}

      <div className="cbw-msg__body">
        <div
          className={cx(
            'cbw-bubble',
            isUser ? 'cbw-bubble--user' : 'cbw-bubble--bot',
            isError && 'cbw-bubble--error',
          )}
        >
          {(hasContent || isError || hasCustom) && body}
          {isStreaming && (
            <span className={cx('cbw-cursor', hasContent && 'cbw-cursor--inline')} aria-hidden />
          )}

          {message.attachments && message.attachments.length > 0 && (
            <div className="cbw-bubble__attachments" aria-label={labels.attachmentsLabel}>
              {message.attachments.map((attachment) => (
                <AttachmentChip key={attachment.id} attachment={attachment} />
              ))}
            </div>
          )}
        </div>

        <div className="cbw-msg__meta">
          <span className="cbw-msg__time">{formatTime(message.createdAt)}</span>

          {!isUser && !isError && !isStreaming && hasContent && (
            <>
              <button
                type="button"
                className="cbw-iconbtn cbw-iconbtn--sm"
                onClick={copy}
                aria-label={copied ? labels.copied : labels.copy}
                title={copied ? labels.copied : labels.copy}
              >
                {copied ? <CheckIcon width={15} height={15} /> : <CopyIcon width={15} height={15} />}
              </button>
              <FeedbackButtons message={message} labels={labels} onVote={onVote} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
